import { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { MdAdd, MdDragIndicator } from "react-icons/md";
import ButtonElement from "../../Common/ButtonElement";
import ToDoCard from "./components/ToDoCards";
import AddTicket from "./components/AddTicket";
import TicketDetails from "./components/TicketDetails";
import SearchBox from "../components/SearchComponent";
import SearchableDropdown from "../../Common/SearchDropdown";
import { taskManagementApi, updateTaskAction } from "../../actions/taskManagement";
import { useApiRequest } from "../../hooks/useApiRequest";
import { commonOnSelect, formatListItems } from "../../utils";
import "../style/taskManagement.css";

// Default columns based on your backend statuses
const columns = [
    { title: "To Do", color: "#e3f2fd" }, // Light blue
    { title: "In Progress", color: "#fff3e0" }, // Light orange
    { title: "Review", color: "#f3e5f5" }, // Light purple
    { title: "Completed", color: "#e8f5e8" }  // Light green
];

const priorityOptions = [
    { id: "High", name: "High" },
    { id: "Medium", name: "Medium" },
    { id: "Low", name: "Low" }
];

const TaskManagement = () => {
    const { request } = useApiRequest();
    const { request: taskRequest, loading } = useApiRequest();

    const [state, setState] = useState({
        tasks: [],
        showAddTicket: false,
        selectedTask: null,
        taskStatuses: [],
        assigneeOptions: [],
        comments: [],
        ccMembers: [],
        assignedCcMembers: [],
        status: "",
        statusId: "",
        assignee: "",
        assigneeId: "",
        priority: "",
        search: "",
    });

    const { tasks, showAddTicket, selectedTask, taskStatuses, ccMembers, search, assignee, assigneeId, priority, priorityId, status, statusId } = state;

    // Fetch tasks from backend
    const fetchTasks = async () => {
        let query = `?page=1&limit=200`;
        if (search) query = `${query}&search=${search}`;
        if (assigneeId) query = `${query}&assigneeId=${assigneeId}`;
        if (priorityId) query = `${query}&priority=${priority}`;
        if (statusId) query = `${query}&status=${statusId}`;

        const url = `${taskManagementApi.getTasks}${query}`;
        await taskRequest({
            url,
            showToast: false,
            onSuccess: (response) => setState(prev => ({ ...prev, tasks: response?.data?.tasks ?? [] }))
        })
    }

    const fetchAssigneeOptions = async () => {
        await request({
            url: taskManagementApi.getAssignees,
            showToast: false,
            onSuccess: (response) => {
                setState(prev => ({ ...prev, ccMembers: formatListItems(response?.data?.users ?? [], "userId", "name") }));
            }
        })
    }

    const fetchTaskStatus = async () => {
        await request({
            url: taskManagementApi.getTaskStatuses,
            showToast: false,
            onSuccess: (response) => {
                setState(prev => ({ ...prev, taskStatuses: formatListItems(response?.data?.statuses ?? [], "status_id", "status_name") }));
            }
        })
    }

    const onDragEnd = async (result) => {
        const { source, destination } = result || {};
        if (!destination) return;

        const sourceStatus = parseInt(source.droppableId);
        const destinationStatus = parseInt(destination.droppableId);

        const todoColumn = taskStatuses.find(s => s.status_name === "To Do");
        if (todoColumn && destinationStatus === todoColumn.status_id && sourceStatus !== todoColumn.status_id) {
            return;
        }

        if (sourceStatus === destinationStatus) {
            // Same column reordering
            const columnTasks = tasks.filter(t => t.status === sourceStatus);
            const [moved] = columnTasks.splice(source.index, 1);
            columnTasks.splice(destination.index, 0, moved);

            const reordered = tasks.map(t => {
                if (t.status !== sourceStatus) return t;
                return columnTasks.shift();
            });

            setState(prev => ({ ...prev, tasks: reordered }));
            return;
        }

        // Different column - update task status in backend
        const sourceTasks = tasks.filter(t => t.status === sourceStatus);
        const [moved] = sourceTasks.splice(source.index, 1);

        try {
            await updateTaskAction(moved.task_id, { status: destinationStatus });

            const movedUpdated = { ...moved, status: destinationStatus };
            const updated = tasks.map(t => {
                if (t.task_id === moved.task_id) return movedUpdated;
                return t;
            });

            setState(prev => ({ ...prev, tasks: updated }));
        } catch (error) {
            console.error("Error updating task status:", error);
            fetchTasks(); // revert if backend fails
        }
    };


    const handleTaskSuccess = (updatedTask) => {
        // If specific updated fields are provided, merge them locally to avoid pagination issues
        if (updatedTask?.task_id) {
            setState(prev => ({
                ...prev,
                showAddTicket: false,
                tasks: prev.tasks.map(t => {
                    if (t.task_id !== updatedTask.task_id) return t;
                    return {
                        ...t,
                        ...updatedTask,
                        // Ensure nested shapes are preserved/merged sensibly
                        assignee: updatedTask.assignee ? updatedTask.assignee : t.assignee,
                        TaskCcMembers: updatedTask.TaskCcMembers ? updatedTask.TaskCcMembers : t.TaskCcMembers,
                    };
                })
            }));
            return;
        }

        // Fallback: full refresh
        setState(prev => ({ ...prev, showAddTicket: false }));
        fetchTasks();
    }

    const tasksByStatus = useMemo(() => {
        if (tasks?.length && taskStatuses?.length) {
            const result = [];
            taskStatuses?.forEach((taskStatus, index) => {
                result[index] = tasks.filter(task => task.status == taskStatus.status_id);
            })
            return result;
        }
        return [];
    }, [tasks, taskStatuses])

    useEffect(() => {
        fetchTasks();
    }, [search, assignee, priority, status])

    useEffect(() => {
        fetchTaskStatus();
        fetchAssigneeOptions();
    }, []);

    return (
        <>
            <div className="task-search">
                <SearchBox
                    placeholder="Search tasks ..."
                    value={search}
                    onChange={(e) => setState(prev => ({ ...prev, search: e.target.value }))}
                />
            </div>
            <div style={{paddingTop: "110px"}}>
                {/* Header */}
                <div className="kanban-header mt-3 ">
                    <h2 className="kanban-title">Task Management</h2>
                    <div className="d-flex align-items-center gap-3">
                        <SearchableDropdown
                            placeholder={"All Status"}
                            value={status}
                            onSelect={(value) => commonOnSelect({ target: { name: "status", value } }, setState)}
                            options={[{ id: null, name: "All Statuses" }, ...(taskStatuses || []), { id: 5, name: "Overdue" }]}
                        />
                        <SearchableDropdown
                            placeholder={"All Assignees"}
                            value={assignee}
                            onSelect={(value) => commonOnSelect({ target: { name: "assignee", value } }, setState)}
                            options={[{ id: null, name: "All Assignees" }, ...(ccMembers || [])]}
                        />
                        <SearchableDropdown
                            placeholder={"All Priorities"}
                            value={priority}
                            onSelect={(value) => commonOnSelect({ target: { name: "priority", value } }, setState)}
                            options={[{ id: null, name: "All Priorities" }, ...(priorityOptions || [])]}
                        />
                        <ButtonElement
                            text={<><MdAdd size={18} className="me-1" />Add Task</>}
                            cssClass="small-radius"
                            onClick={() => setState(prev => ({ ...prev, showAddTicket: true }))}
                            variant="btn-black"
                        />
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="text-center p-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}

                {/* Kanban Board */}
                {!loading && (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <div className="kanban-board">
                            {taskStatuses.map((column, index) => {
                                return (
                                    column?.id ? <Droppable key={column.id} droppableId={column.id.toString()}>
                                        {(provided) => (
                                            <div
                                                className="kanban-column"
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                data-status={column.name}
                                            >
                                                {/* Column Header */}
                                                <div
                                                    className="column-header"
                                                    style={{ backgroundColor: columns?.[index]?.color || "#f0f0f0" }}
                                                >
                                                    <div className="column-title">
                                                        <MdDragIndicator size={20} className="drag-icon" />
                                                        <span>{column.name || "Unknown"}</span>
                                                    </div>
                                                    <div className="column-count">
                                                        {(tasksByStatus[index] || []).length}
                                                    </div>
                                                </div>
                                                {/* Column Content */}
                                                {(tasksByStatus[index] || []).map((task, index) => (
                                                    <Draggable
                                                        key={task.task_id}
                                                        draggableId={String(task.task_id)}
                                                        index={index}>

                                                        {(provided) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className="task-card-wrapper">
                                                                <ToDoCard
                                                                    title={task.title}
                                                                    description={task.description}
                                                                    statusId={task.status}
                                                                    priority={task.priority}
                                                                    assignee={
                                                                        (task.assignee?.name || "NA")
                                                                            .split(" ")
                                                                            .map(word => word[0])
                                                                            .join("")
                                                                            .toUpperCase()
                                                                    }
                                                                    dueDate={task.status === 4 ? "" : task.due_date}
                                                                    associatedCount={Number(task.ccMembersCount) || 0}
                                                                    commentCount={Number(task.commentsCount) || 0}
                                                                    onClick={() => setState(prev => ({ ...prev, selectedTask: task }))}
                                                                />
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}

                                                {/* Empty state */}
                                                {(tasksByStatus[index] || []).length === 0 && (
                                                    <div className="empty-column">
                                                        <p>No tasks in {column.name || "Unknown"}</p>
                                                    </div>
                                                )}

                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable> : null
                                );
                            })}
                        </div>
                    </DragDropContext>
                )}

                {/* Modals */}
                {showAddTicket && (
                    <AddTicket
                        show={showAddTicket}
                        hideModal={() => setState(prev => ({ ...prev, showAddTicket: false }))}
                        successAction={handleTaskSuccess}
                        taskStatuses={taskStatuses}
                        ccMemberOptions={ccMembers}
                    />
                )}

                {selectedTask && (
                    <TicketDetails
                        show={!!selectedTask}
                        hideModal={() => setState(prev => ({ ...prev, selectedTask: null }))}
                        ticket={selectedTask}
                        onSuccess={handleTaskSuccess}
                        taskStatuses={taskStatuses}
                        ccMemberOptions={ccMembers}
                    />
                )}
            </div>
        </>
    );
};

export default TaskManagement;